import { useNode } from "@craftjs/core";
import customDateFormat from "dateformat";
import React, { useContext, useState } from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  ToggleInput,
  Panel,
  Tabs,
  TextInput,
  Spacer,
  Notice,
  AlignInput,
  RangeNumberInput,
  TabsV2,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";
import { GrigoraPreviewContext } from "../../post-preview/context";
import { GrigoraEditorContext } from "../../editor/context";

export const PostDateSettings = () => {
  const previewContext = useContext(GrigoraPreviewContext);
  const { cmsPosts } = useContext(GrigoraEditorContext) ?? {};
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    dateFormat = "dd/mm/yyyy",
    textColor,
    backgroundColor,
    typoSize,
    typoSizeTablet,
    typoSizeMobile,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoTransform,
    typoStyle,
    typoDecoration,
    typoWeight,
    hasLink,
    customDate,
    prefix,
    suffix,
    linkPost,
    openInNewTab,
    link = "#",
    align,
    hoverTextColor,
    hoverBackgroundColor,
    transition,
    date,
  } = props;

  const [currentDate, setCurrentDate] = useState(
    customDateFormat(
      previewContext?.postData?.first_published
        ? new Date(previewContext?.postData?.first_published * 1000)
        : date
        ? new Date(date)
        : cmsPosts?.[0]?.first_published
        ? new Date(cmsPosts?.[0]?.first_published * 1000)
        : Date.now(),
    ),
  );
  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            isResetVisible={false}
            value={dateFormat}
            onChange={(val) => {
              setProp((props) => {
                props.dateFormat = val;
              });
            }}
            options={[
              {
                label: customDateFormat(currentDate, "dd/mm/yyyy"),
                value: "dd/mm/yyyy",
              },
              {
                label: customDateFormat(currentDate, "mm/d/yyyy"),
                value: "mm/d/yyyy",
              },
              {
                label: customDateFormat(currentDate, "yyyy-mm-d"),
                value: "yyyy-mm-d",
              },
              {
                label: customDateFormat(currentDate, "yyyy, mmm d"),
                value: "yyyy, mmm d",
              },
              {
                label: customDateFormat(currentDate, "yyyy HH:MM TT"),
                value: "yyyy HH:MM TT",
              },
              {
                label: customDateFormat(currentDate, "yyyy hh:MM"),
                value: "yyyy hh:MM",
              },
              {
                label: customDateFormat(currentDate, "mmm, d yyyy"),
                value: "mmm, d yyyy",
              },
              {
                label: customDateFormat(currentDate, "mmm, d yyyy HH:MM TT"),
                value: "mmm, d yyyy HH:MM TT",
              },
              {
                label: customDateFormat(currentDate, "d, mmm, yyyy"),
                value: "d, mmm, yyyy",
              },
              {
                label: customDateFormat(currentDate, "d, mmm, yyyy hh:MM"),
                value: "d, mmm, yyyy hh:MM",
              },
              {
                label: customDateFormat(currentDate, "d, mmm, yyyy HH:MM TT"),
                value: "d, mmm, yyyy HH:MM TT",
              },
              {
                label: customDateFormat(currentDate, "m/d/yy"),
                value: "m/d/yy",
              },
              {
                label: customDateFormat(currentDate, "m, ddd yyyy"),
                value: "m, ddd yyyy",
              },
              {
                label: customDateFormat(currentDate, "yyyy, m ddd"),
                value: "yyyy, m ddd",
              },
              {
                label: customDateFormat(currentDate, "ddd, m yyyy"),
                value: "ddd, m yyyy",
              },
              {
                label: "Custom",
                value: "custom",
              },
            ]}
            label={"Date Format"}
          />
          {dateFormat === "custom" && (
            <>
              <Notice
                text={
                  <>
                    Grigora uses node-dateformat parser to format dates. For
                    custom formatting, please follow this{" "}
                    <a
                      className="underline"
                      href="https://github.com/felixge/node-dateformat#dateformat"
                      target={"_blank"}
                      rel="noreferrer"
                    >
                      guide
                    </a>
                    .
                  </>
                }
                status="info"
              />
              <TextInput
                minLength={INPUT_LIMITS["post-date-custom-format"].minlength}
                maxLength={INPUT_LIMITS["post-date-custom-format"].maxlength}
                label="Custom Format"
                resetValue={undefined}
                value={customDate}
                onChange={(val) => {
                  setProp((props) => {
                    props.customDate = val;
                  });
                }}
              />
            </>
          )}
          <AlignInput
            resetValue={undefined}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            label={"Align"}
          />
          <TextInput
            minLength={INPUT_LIMITS["post-prefix"].minlength}
            maxLength={INPUT_LIMITS["post-prefix"].maxlength}
            label="Prefix"
            value={prefix}
            onChange={(val) => {
              setProp((props) => {
                props.prefix = val;
              });
            }}
          />
          <TextInput
            minLength={INPUT_LIMITS["post-suffix"].minlength}
            maxLength={INPUT_LIMITS["post-suffix"].maxlength}
            label="Suffix"
            value={suffix}
            onChange={(val) => {
              setProp((props) => {
                props.suffix = val;
              });
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="Link to Post"
            value={linkPost}
            onChange={(val) => {
              setProp((props) => {
                props.linkPost = val;
              });
            }}
          />
          {linkPost && (
            <>
              <ToggleInput
                resetValue={undefined}
                label="Open in new tab"
                value={openInNewTab}
                onChange={(val) => {
                  setProp((props) => {
                    props.openInNewTab = val;
                  });
                }}
              />
            </>
          )}
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoSizeTablet },
            { key: typoSizeMobile },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            resetValue={undefined}
            value={typoSize}
            isResponsive={true}
            valueTablet={typoSizeTablet}
            valueMobile={typoSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.typoSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.typoSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              "100",
              "200",
              "300",
              "400",
              "500",
              "600",
              "700",
              "800",
              "900",
            ].map(function (item) {
              return {
                label: item,
                value: item,
              };
            })}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={["uppercase", "lowercase", "capitalize", "none"].map(
              function (item) {
                return {
                  label: item,
                  value: item,
                };
              },
            )}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={["underline", "overline", "none"].map(function (item) {
              return {
                label: item,
                value: item,
              };
            })}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={["normal", "italic"].map(function (item) {
              return {
                label: item,
                value: item,
              };
            })}
            label="Style"
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorStates({
          textColorPropKey: "textColor",
          textColorProp: textColor,
          backgroundColorPropKey: "backgroundColor",
          backgroundColorProp: backgroundColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          textColorPropKey: "hoverTextColor",
          textColorProp: hoverTextColor,
          backgroundColorPropKey: "hoverBackgroundColor",
          backgroundColorProp: hoverBackgroundColor,
        }),
      },
    ];

    function handleColorStates({
      textColorPropKey,
      textColorProp,
      backgroundColorPropKey,
      backgroundColorProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={textColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[textColorPropKey] = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={backgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[backgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(textColorPropKey === "hoverTextColor" ||
            backgroundColorPropKey === "hoverBackgroundColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: textColor },
            { key: backgroundColor },
            { key: hoverTextColor },
            { key: hoverBackgroundColor },
          ])}
          id={`block-setting-${id}-colors`}
          title={"Colors"}
          initialOpen={true}
        >
          <TabsV2 items={colorTabs} />
        </Panel>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }

  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: dateFormat },
        { key: customDate },
        { key: align },
        { key: prefix },
        { key: suffix },
        { key: linkPost },
        { key: openInNewTab },
        { key: typoSize },
        { key: typoSizeTablet },
        { key: typoSizeMobile },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
        { key: hoverTextColor },
        { key: hoverBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([]),
    },
  ];

  return (
    <div>
      <Tabs type="block-setting" id={`block-settings-tab-${id}`} items={tabs} />
    </div>
  );
};
